import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { contactCenterAPI, phoneNumbersAPI, customersAPI, communicationsAPI, usersAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { formatTimestampCompact } from '../lib/timezone';
import { toast } from 'sonner';
import {
  PhoneIncoming, PhoneOutgoing, PhoneMissed, Phone, MessageSquare, Loader2,
  Play, Download, Pause, UserPlus, UserCheck, Search, X, Send,
  AlertCircle, Headphones, ExternalLink, User, Mail, MapPin, TrendingUp, ArrowLeft,
  ShieldBan, ShieldCheck, Clock, CheckCircle2, XCircle, MessageCircle, Edit3,
  PhoneCall, Ban, UserCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatPhone(number) {
  if (!number) return '';
  const d = number.replace(/[^\d]/g, '');
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return number;
}

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'unmatched', label: 'Unmatched' },
  { key: 'sms', label: 'SMS' },
  { key: 'calls', label: 'Calls' },
  { key: 'missed', label: 'Missed' },
  { key: 'follow-up', label: 'Follow-Up' },
];

const CALL_OUTCOMES = [
  { value: 'booked', label: 'Booked' },
  { value: 'estimate_scheduled', label: 'Estimate Scheduled' },
  { value: 'follow_up_needed', label: 'Follow Up Needed' },
  { value: 'left_voicemail', label: 'Left Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'spam', label: 'Spam' },
  { value: 'other', label: 'Other' },
];

const CALL_STATUS_STYLES = {
  completed: 'bg-emerald-500/10 text-emerald-600',
  connected: 'bg-blue-500/10 text-blue-600',
  ringing: 'bg-amber-500/10 text-amber-600',
  missed: 'bg-red-500/10 text-red-600',
  rejected: 'bg-zinc-500/10 text-zinc-500',
};

// ── Recording Controls ───────────────────────────────────────────────────────

function RecordingPlayer({ callId }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(phoneNumbersAPI.getRecordingUrl(callId));
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => { toast.error('Failed to load recording'); setPlaying(false); };
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = phoneNumbersAPI.getRecordingUrl(callId, true);
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="flex items-center gap-1 mt-1">
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggle} data-testid={`play-recording-${callId}`}>
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={download} data-testid={`download-recording-${callId}`}>
        <Download className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ── Assign Customer Dialog ───────────────────────────────────────────────────

function AssignDialog({ open, onClose, phone, onAssigned }) {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => { if (!open) { setSearch(''); setCustomers([]); } }, [open]);

  const doSearch = useCallback(async () => {
    if (search.length < 2) { setCustomers([]); return; }
    setLoading(true);
    try {
      const res = await customersAPI.list({ search, page_size: 10 });
      setCustomers(res.data.customers || []);
    } catch { setCustomers([]); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(doSearch, 300); return () => clearTimeout(t); }, [doSearch]);

  const handleAssign = async (customerId) => {
    setAssigning(customerId);
    try {
      const res = await contactCenterAPI.assign(phone, customerId);
      toast.success(`Assigned to ${res.data.customer_name}`);
      onAssigned(res.data);
      onClose();
    } catch { toast.error('Failed to assign'); }
    finally { setAssigning(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="assign-customer-dialog">
        <DialogHeader><DialogTitle>Assign to Customer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search customers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" autoFocus data-testid="assign-customer-search" />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {loading && <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>}
            {!loading && customers.length === 0 && search.length >= 2 && <p className="text-sm text-muted-foreground text-center py-4">No customers found</p>}
            {customers.map(c => (
              <button key={c.id} onClick={() => handleAssign(c.id)} disabled={assigning === c.id}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted text-left" data-testid={`assign-option-${c.id}`}>
                <div>
                  <p className="text-sm font-medium">{c.display_name}</p>
                  {c.primary_phone && <p className="text-xs text-muted-foreground">{formatPhone(c.primary_phone)}</p>}
                </div>
                {assigning === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4 text-muted-foreground" />}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Create Customer Dialog ───────────────────────────────────────────────────

function CreateDialog({ open, onClose, phone, onCreated }) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (!open) setName(''); }, [open]);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Enter a name'); return; }
    setCreating(true);
    try {
      const res = await contactCenterAPI.createCustomer(phone, name.trim());
      toast.success(`Created: ${res.data.customer_name}`);
      onCreated(res.data);
      onClose();
    } catch { toast.error('Failed to create'); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="create-customer-dialog">
        <DialogHeader><DialogTitle>Create Customer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Phone</p>
            <p className="text-sm font-medium">{formatPhone(phone)}</p>
          </div>
          <Input placeholder="Customer name" value={name} onChange={(e) => setName(e.target.value)} autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()} data-testid="create-customer-name" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !name.trim()} size="sm" data-testid="create-customer-submit">
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Thread List Item ─────────────────────────────────────────────────────────

function ThreadListItem({ thread, isSelected, onClick }) {
  const preview = thread.preview || (thread.type === 'call' ? 'Phone call' : 'No messages yet');
  const isUnread = thread.unread_count > 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2.5 border-b border-border/40 transition-colors',
        isSelected ? 'bg-primary/8 border-l-2 border-l-primary' : 'hover:bg-muted/40 border-l-2 border-l-transparent'
      )}
      data-testid={`thread-${thread.phone}`}
    >
      <div className="flex items-start gap-2.5">
        {isUnread && (
          <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" data-testid={`unread-dot-${thread.phone}`} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={cn('text-[13px] truncate leading-tight', isUnread ? 'font-bold text-foreground' : 'font-semibold')}>
              {thread.customer_name || formatPhone(thread.phone)}
            </p>
            <div className="flex items-center gap-1.5 shrink-0">
              {isUnread && thread.unread_count > 1 && (
                <span className="text-[10px] font-bold bg-primary text-primary-foreground rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid={`unread-badge-${thread.phone}`}>
                  {thread.unread_count > 9 ? '9+' : thread.unread_count}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground tabular-nums">{timeAgo(thread.last_activity)}</span>
            </div>
          </div>
          {thread.customer_name && (
            <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{formatPhone(thread.phone)}</p>
          )}
          <p className={cn('text-xs truncate mt-1 leading-snug', isUnread ? 'text-foreground/70 font-medium' : 'text-muted-foreground/80')}>
            {preview}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            {thread.sms_consent === 'opted_out' && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300 leading-none flex items-center gap-0.5" data-testid={`opted-out-badge-${thread.phone}`}>
                <ShieldBan className="w-2.5 h-2.5" /> Opted out
              </span>
            )}
            {!thread.matched && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 leading-none">
                Unmatched
              </span>
            )}
            {thread.missed_count > 0 && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300 leading-none">
                {thread.missed_count} missed
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ── Timeline: SMS Bubble ─────────────────────────────────────────────────────

function SmsBubble({ event, isGrouped, isLastInGroup }) {
  const isOutbound = event.direction === 'outbound';
  const failed = event.sms_status === 'failed' || event.sms_status === 'undelivered';

  return (
    <div className={cn(
      isGrouped ? 'pt-2' : 'pt-3 first:pt-0',
      !isLastInGroup && isGrouped ? '' : 'pb-3 border-b border-border/15',
    )} data-testid={`sms-bubble-${event.id}`}>
      {isOutbound && !isGrouped && (
        <p className="text-[10px] font-medium text-muted-foreground/60 mb-0.5">{event.user_name || 'You'}</p>
      )}
      <div className={cn(
        'text-sm leading-relaxed',
        isOutbound
          ? failed
            ? 'pl-3 border-l-2 border-l-red-300 dark:border-l-red-700'
            : 'pl-3 border-l-2 border-l-primary/30'
          : ''
      )}>
        <p className={cn(
          'whitespace-pre-wrap break-words',
          failed ? 'text-red-700 dark:text-red-400' : 'text-foreground'
        )}>{event.body}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[10px] text-muted-foreground/50 tabular-nums">
            {formatTimestampCompact(event.timestamp)}
          </span>
          {failed && (
            <span className="flex items-center gap-0.5 text-[10px] text-red-500 font-medium">
              <AlertCircle className="w-3 h-3" /> Failed
            </span>
          )}
          {isOutbound && !failed && (
            <span className="text-[10px] text-muted-foreground/50">· Sent</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Timeline: Call Entry ─────────────────────────────────────────────────────

function callLabel(event) {
  const dir = event.direction === 'inbound' ? 'Incoming' : 'Outgoing';
  if (event.call_status === 'missed') return 'Missed Call';
  if (event.call_status === 'rejected') return 'Rejected Call';
  return `${dir} Call`;
}

function CallEntry({ event, onOutcomeSet }) {
  const isInbound = event.direction === 'inbound';
  const isMissed = event.call_status === 'missed';
  const Icon = isMissed ? PhoneMissed : isInbound ? PhoneIncoming : PhoneOutgoing;
  const accentColor = isMissed ? 'border-l-red-400' : isInbound ? 'border-l-blue-300' : 'border-l-emerald-300';
  const [showOutcome, setShowOutcome] = useState(false);
  const [saving, setSaving] = useState(false);

  const tagOutcome = async (outcome) => {
    setSaving(true);
    try {
      await contactCenterAPI.tagCallOutcome(event.call_id, outcome);
      onOutcomeSet?.(event.call_id, outcome);
      setShowOutcome(false);
    } catch { toast.error('Failed to save outcome'); }
    finally { setSaving(false); }
  };

  const outcomeLabel = event.outcome ? CALL_OUTCOMES.find(o => o.value === event.outcome)?.label || event.outcome : null;

  return (
    <div className="mt-3 first:mt-0" data-testid={`call-entry-${event.id}`}>
      <div className={cn(
        'flex items-center gap-2.5 px-3 py-2 rounded text-xs border-l-2',
        accentColor,
        isMissed
          ? 'bg-red-50/40 dark:bg-red-950/10'
          : 'bg-muted/20'
      )}>
        <Icon className={cn('w-3.5 h-3.5 shrink-0', isMissed ? 'text-red-400' : isInbound ? 'text-blue-400' : 'text-emerald-400')} />
        <span className={cn('font-medium', isMissed ? 'text-red-600 dark:text-red-400' : 'text-foreground/80')}>
          {callLabel(event)}
        </span>
        {event.duration_seconds > 0 && (
          <span className="text-muted-foreground/60 tabular-nums">{formatDuration(event.duration_seconds)}</span>
        )}
        {outcomeLabel && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary leading-none" data-testid={`outcome-badge-${event.id}`}>
            {outcomeLabel}
          </span>
        )}
        <span className="text-muted-foreground/50 tabular-nums ml-auto">{formatTimestampCompact(event.timestamp)}</span>
        {event.user_name && <span className="text-muted-foreground/50">· {event.user_name}</span>}
        {!event.outcome && (
          <button
            onClick={() => setShowOutcome(!showOutcome)}
            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1"
            data-testid={`tag-outcome-btn-${event.id}`}
            title="Tag outcome"
          >
            Tag
          </button>
        )}
        {event.has_recording && event.call_id && (
          <RecordingPlayer callId={event.call_id} />
        )}
      </div>
      {showOutcome && (
        <div className="ml-6 mt-1 flex flex-wrap gap-1" data-testid={`outcome-picker-${event.id}`}>
          {CALL_OUTCOMES.map(o => (
            <button
              key={o.value}
              onClick={() => tagOutcome(o.value)}
              disabled={saving}
              className="text-[10px] px-2 py-1 rounded border border-border/60 hover:bg-muted transition-colors"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SMS Composer ─────────────────────────────────────────────────────────────

function SmsComposer({ phone, onSent, smsConsent }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const isOptedOut = smsConsent === 'opted_out';

  const handleSend = async () => {
    if (!text.trim() || isOptedOut) return;
    setSending(true);
    try {
      const res = await contactCenterAPI.sendSms(phone, text.trim());
      if (res.data.ok) {
        toast.success('Message sent');
        setText('');
        onSent();
      } else {
        toast.error('Message failed to send — may need A2P approval');
        onSent();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.includes('opted out')) {
        toast.error(detail);
      } else {
        toast.error('Failed to send message');
      }
    } finally { setSending(false); }
  };

  if (isOptedOut) {
    return (
      <div className="border-t border-border px-4 py-3 bg-card" data-testid="sms-composer">
        <div className="flex items-center gap-2 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-3 py-2.5" data-testid="sms-opted-out-notice">
          <ShieldBan className="w-4 h-4 text-red-500 shrink-0" />
          <div>
            <p className="text-xs font-medium text-red-700 dark:text-red-300">SMS blocked — contact opted out</p>
            <p className="text-[11px] text-red-600/80 dark:text-red-400/80 mt-0.5">This contact must reply START to receive messages again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border px-4 py-3 bg-card" data-testid="sms-composer">
      <div className="flex items-end gap-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Type a message…"
          rows={1}
          className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 min-h-[40px] max-h-[120px]"
          style={{ height: 'auto', overflow: 'hidden' }}
          onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
          data-testid="sms-input"
        />
        <Button
          onClick={handleSend}
          disabled={sending || !text.trim()}
          size="sm"
          className="h-[40px] px-4 shrink-0"
          data-testid="sms-send-btn"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send</>}
        </Button>
      </div>
    </div>
  );
}

// ── Contact Panel (Right) ────────────────────────────────────────────────────

function ContactPanel({ detail, onAssign, onCreate }) {
  const navigate = useNavigate();
  if (!detail) return null;

  const consentLabel = detail.sms_consent === 'opted_out' ? 'Opted Out'
    : detail.sms_consent === 'opted_in' ? 'Subscribed' : 'Unknown';
  const consentColor = detail.sms_consent === 'opted_out'
    ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'
    : detail.sms_consent === 'opted_in'
      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300'
      : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400';
  const ConsentIcon = detail.sms_consent === 'opted_out' ? ShieldBan : ShieldCheck;

  // Granular consent for split display
  const mktConsent = detail.sms_marketing_consent || detail.sms_consent || 'unknown';
  const txnConsent = detail.sms_transactional_consent || 'opted_in';
  const mktLabel = mktConsent === 'opted_out' ? 'Blocked' : mktConsent === 'opted_in' ? 'Allowed' : 'Unknown';
  const txnLabel = txnConsent === 'opted_out' ? 'Blocked' : 'Allowed';
  const mktColor = mktConsent === 'opted_out' ? 'text-red-600' : mktConsent === 'opted_in' ? 'text-emerald-600' : 'text-zinc-400';
  const txnColor = txnConsent === 'opted_out' ? 'text-red-600' : 'text-emerald-600';

  return (
    <div className="p-4 space-y-5" data-testid="contact-panel">
      {/* Phone */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Phone</p>
        <p className="text-sm font-semibold font-mono">{formatPhone(detail.phone)}</p>
      </div>

      {/* SMS Consent — Split Marketing / Transactional */}
      <div data-testid="sms-consent-status">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">SMS Consent</p>
        <div className="space-y-1">
          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded ${consentColor}`}>
            <ConsentIcon className="w-3 h-3" />
            {consentLabel}
          </span>
          <div className="flex flex-col gap-0.5 mt-1.5">
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-muted-foreground w-20">Marketing:</span>
              <span className={`font-medium ${mktColor}`} data-testid="sms-marketing-consent">{mktLabel}</span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-muted-foreground w-20">Service/Ops:</span>
              <span className={`font-medium ${txnColor}`} data-testid="sms-transactional-consent">{txnLabel}</span>
            </div>
            {mktConsent === 'opted_out' && txnConsent !== 'opted_out' && (
              <p className="text-[9px] text-amber-600 mt-0.5" data-testid="consent-explanation">Customer opted out of marketing but can still receive service messages.</p>
            )}
            {txnConsent === 'opted_out' && (
              <p className="text-[9px] text-red-600 mt-0.5" data-testid="consent-blocked-warning">All messaging blocked. Customer must text START to re-enable.</p>
            )}
          </div>
        </div>
      </div>

      {detail.matched ? (
        <>
          <div className="border-t border-border/50 pt-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{detail.customer_name}</p>
                <p className="text-[11px] text-muted-foreground">Customer</p>
              </div>
            </div>

            {detail.customer_email && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Mail className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{detail.customer_email}</span>
              </div>
            )}
            {detail.customer_address && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{detail.customer_address}</span>
              </div>
            )}
          </div>

          <Button variant="outline" size="sm" className="w-full" onClick={() => navigate(`/customers/${detail.customer_id}`)} data-testid="open-customer-btn">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open Customer
          </Button>
        </>
      ) : (
        <>
          {/* Unmatched — clear CTA */}
          <div className="border-t border-border/50 pt-4">
            <div className="rounded-md border border-amber-200 bg-amber-50/80 dark:border-amber-800 dark:bg-amber-950/20 p-3 mb-4">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Unmatched Number</p>
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">This number isn't linked to any customer.</p>
            </div>

            <div className="space-y-2">
              <Button size="sm" className="w-full" onClick={onCreate} data-testid="create-customer-action">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Create Customer
              </Button>
              <Button variant="outline" size="sm" className="w-full" onClick={onAssign} data-testid="assign-customer-action">
                <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Assign to Existing
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Follow-Up Queue ─────────────────────────────────────────────────────────

function formatAge(seconds) {
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  const days = Math.floor(seconds / 86400);
  return days === 1 ? '1 day' : `${days} days`;
}

function ageUrgency(seconds) {
  if (seconds < 3600) return 'text-foreground'; // <1h — normal
  if (seconds < 14400) return 'text-amber-600 dark:text-amber-400'; // 1-4h — warning
  return 'text-red-600 dark:text-red-400 font-semibold'; // >4h — urgent
}

const FU_STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: PhoneCall },
  resolved:    { label: 'Resolved',    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle2 },
  dismissed:   { label: 'Dismissed',   color: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400', icon: Ban },
};

function FollowUpNoteDialog({ open, onClose, callId, currentNotes, onSaved }) {
  const [notes, setNotes] = useState(currentNotes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setNotes(currentNotes || ''); }, [open, currentNotes]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await contactCenterAPI.updateFollowUp(callId, { follow_up_notes: notes });
      toast.success('Notes saved');
      onSaved(notes);
      onClose();
    } catch { toast.error('Failed to save notes'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="follow-up-note-dialog">
        <DialogHeader><DialogTitle>Follow-Up Notes</DialogTitle></DialogHeader>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about follow-up attempts..."
          rows={4}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
          data-testid="follow-up-notes-input"
        />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="follow-up-notes-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpAssignDialog({ open, onClose, callId, onAssigned }) {
  const [teamUsers, setTeamUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingUsers(true);
    usersAPI.list().then(r => setTeamUsers(r.data || [])).catch(() => {}).finally(() => setLoadingUsers(false));
  }, [open]);

  const handleAssign = async (userId) => {
    try {
      await contactCenterAPI.updateFollowUp(callId, { follow_up_by: userId });
      const user = teamUsers.find(u => u.id === userId);
      toast.success(`Assigned to ${user?.name || 'team member'}`);
      onAssigned(userId, user?.name);
      onClose();
    } catch { toast.error('Failed to assign'); }
  };

  const handleUnassign = async () => {
    try {
      await contactCenterAPI.updateFollowUp(callId, { follow_up_by: '' });
      toast.success('Unassigned');
      onAssigned(null, null);
      onClose();
    } catch { toast.error('Failed to unassign'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="follow-up-assign-dialog">
        <DialogHeader><DialogTitle>Assign Follow-Up</DialogTitle></DialogHeader>
        <div className="max-h-60 overflow-y-auto space-y-1">
          {loadingUsers && <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>}
          <button onClick={handleUnassign}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-muted text-left text-sm text-muted-foreground"
            data-testid="follow-up-unassign">
            <XCircle className="h-4 w-4" /> Unassign
          </button>
          {teamUsers.map(u => (
            <button key={u.id} onClick={() => handleAssign(u.id)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-muted text-left text-sm"
              data-testid={`follow-up-assign-${u.id}`}>
              <UserCircle className="h-4 w-4 text-muted-foreground" /> {u.name}
              <span className="text-xs text-muted-foreground ml-auto">{u.role}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpQueue() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('actionable');
  const [noteDialog, setNoteDialog] = useState({ open: false, callId: null, notes: '' });
  const [assignDialog, setAssignDialog] = useState({ open: false, callId: null });

  const fetchQueue = useCallback(async () => {
    try {
      const res = await contactCenterAPI.followUpQueue({ status_filter: statusFilter });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load follow-up queue');
      setItems([]);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { setLoading(true); fetchQueue(); }, [fetchQueue]);

  // Poll every 30s
  useEffect(() => {
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const updateStatus = async (callId, newStatus) => {
    try {
      await contactCenterAPI.updateFollowUp(callId, { follow_up_status: newStatus });
      toast.success(`Marked as ${FU_STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchQueue();
    } catch { toast.error('Failed to update status'); }
  };

  const handleCallBack = (item) => {
    window.dispatchEvent(new CustomEvent('softphone:dial', {
      detail: {
        number: item.from_number,
        customerId: item.customer_id || null,
        customerName: item.customer_name || null,
      },
    }));
    // Auto-set to in_progress when calling back
    if (item.follow_up_status === 'pending') {
      updateStatus(item.id, 'in_progress');
    }
  };

  const handleNoteSaved = (notes) => {
    setItems(prev => prev.map(it => it.id === noteDialog.callId ? { ...it, follow_up_notes: notes } : it));
  };

  const handleAssigned = (userId, userName) => {
    setItems(prev => prev.map(it => it.id === assignDialog.callId
      ? { ...it, follow_up_by: userId, follow_up_by_name: userName } : it));
  };

  const FILTER_OPTIONS = [
    { value: 'actionable', label: 'Actionable' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'dismissed', label: 'Dismissed' },
    { value: 'all', label: 'All' },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="follow-up-queue">
      {/* Filter bar */}
      <div className="px-4 py-3 border-b border-border bg-card shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <PhoneMissed className="h-4 w-4 text-red-500" />
            <h2 className="text-sm font-semibold">Follow-Up Queue</h2>
            <span className="text-xs text-muted-foreground">({total})</span>
          </div>
          <div className="flex gap-1">
            {FILTER_OPTIONS.map(f => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={cn(
                  'px-2 py-1 text-[11px] font-medium rounded-md transition-colors',
                  statusFilter === f.value
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
                data-testid={`fu-filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Queue list */}
      <div className="flex-1 overflow-y-auto" data-testid="follow-up-list">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground px-4">
            <CheckCircle2 className="h-10 w-10 mb-3 opacity-20" />
            <p className="text-sm font-medium">All clear</p>
            <p className="text-xs mt-1">No missed calls need follow-up right now.</p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {items.map(item => {
              const cfg = FU_STATUS_CONFIG[item.follow_up_status] || FU_STATUS_CONFIG.pending;
              const StatusIcon = cfg.icon;
              return (
                <div key={item.id} className="px-4 py-3 hover:bg-muted/30 transition-colors" data-testid={`fu-item-${item.id}`}>
                  <div className="flex items-start gap-3">
                    {/* Left: caller info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold font-mono truncate">{formatPhone(item.from_number)}</span>
                        {item.customer_name && (
                          <span className="text-xs text-muted-foreground truncate max-w-[140px]" data-testid={`fu-customer-${item.id}`}>
                            {item.customer_name}
                          </span>
                        )}
                        {!item.customer_name && !item.customer_id && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            Unmatched
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                        <span>{item.started_at ? new Date(item.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                        <span className={ageUrgency(item.age_seconds)} data-testid={`fu-age-${item.id}`}>
                          <Clock className="inline h-3 w-3 mr-0.5 -mt-px" />
                          {formatAge(item.age_seconds)} ago
                        </span>
                        {item.autotext_sent && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300" data-testid={`fu-autotext-${item.id}`}>
                            <MessageCircle className="h-2.5 w-2.5" /> Auto-text sent
                          </span>
                        )}
                        {item.has_recording && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                            <Headphones className="h-2.5 w-2.5" /> VM
                          </span>
                        )}
                      </div>
                      {/* Notes preview */}
                      {item.follow_up_notes && (
                        <p className="text-[11px] text-muted-foreground mt-1 truncate max-w-xs italic" data-testid={`fu-notes-preview-${item.id}`}>
                          {item.follow_up_notes}
                        </p>
                      )}
                    </div>

                    {/* Right: status + assignment + actions */}
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="flex items-center gap-2">
                        {/* Status badge */}
                        <span className={cn('inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded', cfg.color)} data-testid={`fu-status-${item.id}`}>
                          <StatusIcon className="h-3 w-3" /> {cfg.label}
                        </span>
                        {/* Assigned user */}
                        {item.follow_up_by_name && (
                          <button
                            onClick={() => setAssignDialog({ open: true, callId: item.id })}
                            className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                            data-testid={`fu-assigned-${item.id}`}
                          >
                            <UserCircle className="h-3 w-3" /> {item.follow_up_by_name}
                          </button>
                        )}
                      </div>

                      {/* Quick actions */}
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Call back"
                          onClick={() => handleCallBack(item)} data-testid={`fu-callback-${item.id}`}>
                          <Phone className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Mark resolved"
                          onClick={() => updateStatus(item.id, 'resolved')} data-testid={`fu-resolve-${item.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Dismiss"
                          onClick={() => updateStatus(item.id, 'dismissed')} data-testid={`fu-dismiss-${item.id}`}>
                          <Ban className="h-3.5 w-3.5 text-zinc-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Add/Edit notes"
                          onClick={() => setNoteDialog({ open: true, callId: item.id, notes: item.follow_up_notes || '' })}
                          data-testid={`fu-notes-${item.id}`}>
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                        {!item.follow_up_by_name && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Assign"
                            onClick={() => setAssignDialog({ open: true, callId: item.id })}
                            data-testid={`fu-assign-${item.id}`}>
                            <UserCircle className="h-3.5 w-3.5 text-blue-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <FollowUpNoteDialog
        open={noteDialog.open}
        onClose={() => setNoteDialog({ open: false, callId: null, notes: '' })}
        callId={noteDialog.callId}
        currentNotes={noteDialog.notes}
        onSaved={handleNoteSaved}
      />
      <FollowUpAssignDialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, callId: null })}
        callId={assignDialog.callId}
        onAssigned={handleAssigned}
      />
    </div>
  );
}

// ── Main Contact Center Page ─────────────────────────────────────────────────

export default function CallsInbox() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [setupState, setSetupState] = useState(null);

  const [selectedPhone, setSelectedPhone] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const timelineEndRef = useRef(null);
  const pollRef = useRef(null);
  const lastPollRef = useRef(new Date().toISOString());

  // ── Check phone setup state ──
  useEffect(() => {
    communicationsAPI.setupState().then(r => setSetupState(r.data)).catch(() => {});
  }, []);

  // ── Fetch threads ──
  const fetchThreads = useCallback(async () => {
    try {
      const params = { tab, page_size: 100 };
      if (search.trim()) params.search = search.trim();
      const res = await contactCenterAPI.threads(params);
      setThreads(res.data.threads || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load threads');
      setThreads([]);
    } finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { setLoading(true); fetchThreads(); }, [fetchThreads]);

  // ── Polling for real-time updates ──
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await contactCenterAPI.poll(lastPollRef.current);
        if (res.data.has_updates) {
          fetchThreads();
          if (selectedPhone) fetchDetail(selectedPhone);
        }
      } catch { /* silent */ }
      lastPollRef.current = new Date().toISOString();
    };
    pollRef.current = setInterval(poll, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchThreads, selectedPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch thread detail ──
  const fetchDetail = useCallback(async (phone) => {
    if (!phone) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const res = await contactCenterAPI.threadDetail(phone);
      setDetail(res.data);
    } catch {
      toast.error('Failed to load thread');
      setDetail(null);
    } finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { fetchDetail(selectedPhone); }, [selectedPhone, fetchDetail]);

  // ── Mark as read when thread is selected ──
  useEffect(() => {
    if (selectedPhone) {
      contactCenterAPI.markRead(selectedPhone).catch(() => {});
      // Optimistically update unread in thread list
      setThreads(prev => prev.map(t => t.phone === selectedPhone ? { ...t, unread_count: 0 } : t));
    }
  }, [selectedPhone]);

  // Auto-scroll to bottom when detail loads
  useEffect(() => {
    if (detail && timelineEndRef.current) {
      timelineEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [detail]);

  // ── Handlers ──
  const handleAssigned = (data) => {
    fetchThreads();
    fetchDetail(selectedPhone);
  };

  const handleCreated = (data) => {
    fetchThreads();
    fetchDetail(selectedPhone);
  };

  const handleSmsSent = () => {
    fetchDetail(selectedPhone);
    setTimeout(fetchThreads, 1000);
  };

  const handleClickToCall = (phone) => {
    const thread = threads.find(t => t.phone === phone);
    window.dispatchEvent(new CustomEvent('softphone:dial', {
      detail: {
        number: phone,
        customerId: thread?.customer_id || null,
        customerName: thread?.customer_name || null,
      },
    }));
  };

  const handleOutcomeSet = (callId, outcome) => {
    // Update the event in the detail view optimistically
    if (detail) {
      setDetail(prev => ({
        ...prev,
        events: prev.events.map(e => e.id === callId ? { ...e, outcome } : e),
      }));
    }
  };

  const selectedThread = threads.find(t => t.phone === selectedPhone);

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="contact-center-page">
        {/* Setup state banners */}
        {setupState && !setupState.enabled && (
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between shrink-0" data-testid="phone-not-enabled-banner">
            <p className="text-sm text-amber-800">Phone System is not enabled. Enable it in Billing to use the Contact Center.</p>
            <Button size="sm" variant="outline" onClick={() => navigate('/billing')}>Go to Billing</Button>
          </div>
        )}
        {setupState && setupState.enabled && !setupState.setup_complete && (
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between shrink-0" data-testid="setup-incomplete-banner">
            <p className="text-sm text-blue-800">
              {setupState.active_numbers === 0
                ? 'Phone setup incomplete — you need phone numbers to receive calls.'
                : !setupState.has_call_flow
                  ? 'Phone setup incomplete — configure a call flow to route incoming calls.'
                  : 'Phone setup is almost done. Complete setup to finalize your phone system.'}
            </p>
            <Button size="sm" onClick={() => navigate('/phone-setup')} data-testid="continue-setup-btn">
              {setupState.active_numbers === 0 ? 'Get Numbers' : 'Complete Setup'}
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="px-4 py-3 border-b border-border bg-background shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold tracking-tight" data-testid="contact-center-title">Contact Center</h1>
              <p className="text-xs text-muted-foreground">{total} conversation{total !== 1 ? 's' : ''}</p>
            </div>
            <Button variant="outline" size="sm" className="text-xs h-7 hidden sm:inline-flex" onClick={() => navigate('/contact-center/analytics')} data-testid="analytics-btn">
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Analytics
            </Button>
          </div>
        </div>

        {/* 3-Panel Layout OR Follow-Up Queue */}
        {tab === 'follow-up' ? (
          <div className="flex flex-1 min-h-0">
            {/* LEFT: Tab bar stays visible */}
            <div className="w-full md:w-80 md:shrink-0 border-r border-border flex flex-col bg-card md:block hidden">
              <div className="px-2 pt-2 pb-1 border-b border-border/50 shrink-0">
                <div className="flex gap-0.5 bg-muted/50 p-0.5 rounded-lg" data-testid="thread-tabs">
                  {TABS.map(t => (
                    <button
                      key={t.key}
                      onClick={() => { setTab(t.key); setSelectedPhone(null); }}
                      className={cn(
                        'flex-1 px-1.5 py-1 text-[11px] font-medium rounded-md transition-colors text-center',
                        tab === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                      )}
                      data-testid={`tab-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center text-muted-foreground px-4">
                <p className="text-xs text-center opacity-50">Follow-Up Queue is shown on the right</p>
              </div>
            </div>
            {/* Follow-Up Queue takes center + right */}
            <div className="flex-1 flex flex-col min-w-0 bg-background">
              {/* Mobile: tab bar at top */}
              <div className="px-2 pt-2 pb-1 border-b border-border/50 shrink-0 md:hidden">
                <div className="flex gap-0.5 bg-muted/50 p-0.5 rounded-lg" data-testid="thread-tabs-mobile">
                  {TABS.map(t => (
                    <button
                      key={t.key}
                      onClick={() => { setTab(t.key); setSelectedPhone(null); }}
                      className={cn(
                        'flex-1 px-1.5 py-1 text-[11px] font-medium rounded-md transition-colors text-center',
                        tab === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                      )}
                      data-testid={`tab-mob-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <FollowUpQueue />
            </div>
          </div>
        ) : (
        <div className="flex flex-1 min-h-0">

          {/* LEFT: Thread List — full width on mobile, fixed width on desktop */}
          <div className={`${selectedPhone ? 'hidden md:flex' : 'flex'} w-full md:w-80 md:shrink-0 border-r border-border flex-col bg-card`} data-testid="thread-list-panel">
            {/* Tabs */}
            <div className="px-2 pt-2 pb-1 border-b border-border/50 shrink-0">
              <div className="flex gap-0.5 bg-muted/50 p-0.5 rounded-lg" data-testid="thread-tabs">
                {TABS.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={cn(
                      'flex-1 px-1.5 py-1 text-[11px] font-medium rounded-md transition-colors text-center',
                      tab === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid={`tab-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="px-2 py-2 border-b border-border/30 shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search name or phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 text-xs"
                  data-testid="thread-search"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>

            {/* Thread list */}
            <div className="flex-1 overflow-y-auto" data-testid="thread-list">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : threads.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground px-4">
                  <Phone className="h-8 w-8 mb-2 opacity-30" />
                  <p className="text-xs text-center">No conversations found</p>
                </div>
              ) : (
                threads.map(thread => (
                  <ThreadListItem
                    key={thread.phone}
                    thread={thread}
                    isSelected={selectedPhone === thread.phone}
                    onClick={() => setSelectedPhone(thread.phone)}
                  />
                ))
              )}
            </div>
          </div>

          {/* CENTER: Conversation Timeline — full width on mobile, flex on desktop */}
          <div className={`${selectedPhone ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0 bg-background`} data-testid="timeline-panel">
            {!selectedPhone ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center px-6">
                  <div className="w-12 h-12 rounded-lg bg-muted/80 flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="h-6 w-6 opacity-40" />
                  </div>
                  <p className="text-sm font-medium text-foreground/60">No conversation selected</p>
                  <p className="text-xs mt-1 text-muted-foreground">Choose a thread from the list to view messages and call history</p>
                </div>
              </div>
            ) : detailLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : detail ? (
              <>
                {/* Thread header */}
                <div className="px-4 py-3 border-b border-border shrink-0 bg-card" data-testid="thread-header">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 md:hidden" onClick={() => setSelectedPhone(null)} data-testid="thread-back-btn">
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h2 className="text-sm font-bold truncate">{detail.customer_name || formatPhone(detail.phone)}</h2>
                          {detail.events && (() => {
                            const hasSms = detail.events.some(e => e.type === 'sms');
                            const hasCalls = detail.events.some(e => e.type === 'call');
                            const label = hasSms && hasCalls ? 'SMS + Calls' : hasSms ? 'SMS' : 'Calls';
                            return (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground leading-none shrink-0">
                                {label}
                              </span>
                            );
                          })()}
                          {!detail.matched && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 leading-none shrink-0">
                              Unmatched
                            </span>
                          )}
                          {detail.sms_marketing_consent === 'opted_out' && detail.sms_transactional_consent !== 'opted_out' && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 leading-none shrink-0 flex items-center gap-0.5" data-testid="thread-header-marketing-opted-out">
                              <ShieldBan className="w-2.5 h-2.5" /> Mktg opted out
                            </span>
                          )}
                          {detail.sms_transactional_consent === 'opted_out' && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300 leading-none shrink-0 flex items-center gap-0.5" data-testid="thread-header-opted-out">
                              <ShieldBan className="w-2.5 h-2.5" /> All SMS blocked
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">{formatPhone(detail.phone)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleClickToCall(detail.phone)} data-testid="thread-call-btn" title="Call">
                        <Phone className="h-4 w-4" />
                      </Button>
                      {detail.matched && detail.customer_id && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/customers/${detail.customer_id}`)} data-testid="thread-view-customer-btn" title="View customer">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex-1 overflow-y-auto py-4 pr-5" data-testid="timeline-events">
                  <div className="ml-5 pl-4 border-l border-border/15">
                  {detail.events.length === 0 ? (
                    <div className="text-muted-foreground py-8">
                      <p className="text-sm">No communication history</p>
                    </div>
                  ) : (
                    detail.events.map((event, idx) => {
                      const prev = idx > 0 ? detail.events[idx - 1] : null;
                      const next = idx < detail.events.length - 1 ? detail.events[idx + 1] : null;
                      const isGrouped = prev && prev.type === 'sms' && event.type === 'sms' && prev.direction === event.direction;
                      const isLastInGroup = !next || next.type !== 'sms' || next.direction !== event.direction;
                      return event.type === 'sms'
                        ? <SmsBubble key={event.id} event={event} isGrouped={isGrouped} isLastInGroup={isLastInGroup} />
                        : <CallEntry key={event.id} event={event} onOutcomeSet={handleOutcomeSet} />;
                    })
                  )}
                  <div ref={timelineEndRef} />
                  </div>
                </div>

                {/* SMS Composer */}
                <SmsComposer phone={detail.phone} onSent={handleSmsSent} smsConsent={detail.sms_consent} />
              </>
            ) : null}
          </div>

          {/* RIGHT: Contact Panel */}
          <div className="w-64 shrink-0 border-l border-border bg-card hidden xl:block" data-testid="contact-panel-sidebar">
            {selectedPhone && detail ? (
              <ContactPanel
                detail={detail}
                onAssign={() => setAssignOpen(true)}
                onCreate={() => setCreateOpen(true)}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground px-4">
                <p className="text-xs text-center opacity-50">Select a thread to see contact details</p>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Dialogs */}
      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        phone={selectedPhone}
        onAssigned={handleAssigned}
      />
      <CreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        phone={selectedPhone}
        onCreated={handleCreated}
      />
    </Layout>
  );
}
